import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import SubmissionForm from '@/components/feature/SubmissionForm';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { communityStats, discussionRules } from '@/mocks/community';
import CommunityWall from './components/CommunityWall';
import GroupGrid from './components/GroupGrid';

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '讨论区与社群入口',
    description: '爆料吃瓜讨论区、贴吧版面与群聊入口汇总，集中呈现网友观点与粉丝热议。',
    inLanguage: 'zh-CN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
      { '@type': 'ListItem', position: 2, name: '讨论区', item: '/community' },
    ],
  },
];

export default function Community() {
  useDocumentMeta({
    title: '讨论区与社群入口 - 粉丝热议、贴吧与群聊 | 爆料吃瓜',
    description:
      '进入爆料吃瓜讨论区，查看网友对近期热点的最新观点与回复，并按兴趣选择群聊、贴吧或频道入口。也支持直接投稿爆料，我们会人工核实后整理。',
    keywords: '爆料吃瓜讨论区,爆料吃瓜评论区,爆料吃瓜粉丝热议,爆料吃瓜贴吧,爆料吃瓜群聊',
    path: '/community',
    schema,
  });

  const handleRequestEntry = () => {
    const target = document.getElementById('submit');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      const input = document.getElementById('submission-nickname') as HTMLInputElement | null;
      input?.focus({ preventScroll: true });
    }, 620);
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full bg-secondary-950">
          <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
            <Breadcrumbs items={[{ label: '讨论区' }]} tone="inverse" />
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-[42px]">
              讨论区与社群入口
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200/85 md:text-[15px]">
              这里是所有观点的集散地。你可以先看看别人怎么推理，再决定要不要说两句；
              也可以直接滑到下方，按自己的节奏挑一间合适的群聊或贴吧版面。
            </p>

            <dl className="mt-8 grid grid-cols-2 gap-4 border-t border-secondary-800 pt-6 lg:grid-cols-4">
              {communityStats.map((stat) => (
                <div key={stat.label} className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-800 text-accent-400">
                    <i className={`${stat.icon} text-lg`} aria-hidden="true" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-[11px] text-background-300/75">{stat.label}</span>
                    <span className="font-heading text-xl font-black text-background-50">
                      {stat.value}
                      <span className="ml-1 text-xs font-medium text-background-300/75">{stat.unit}</span>
                    </span>
                  </span>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <Reveal>
            <SectionHeading
              eyebrow="Rules"
              title="在这里，讨论方式也是内容的一部分"
              desc="我们只保留四条规则，看起来简单，但基本能解决大部分争吵。"
            />
          </Reveal>
          <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {discussionRules.map((rule, index) => (
              <Reveal key={rule.id} delay={index * 60}>
                <div className="h-full rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${rule.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{rule.title}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">{rule.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="mx-auto w-full px-4 md:px-6">
            <Reveal>
              <SectionHeading
                eyebrow="Wall"
                title="热议墙"
                desc="按话题筛选，看看同一条瓜下面都有哪些不同的推理方向。点赞会即时更新。"
              />
            </Reveal>
            <div className="mt-7">
              <CommunityWall />
            </div>
          </div>
        </section>

        <section className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <Reveal>
            <SectionHeading
              eyebrow="Groups"
              title="社群入口一览"
              desc="每间群的节奏和话题都不一样。点击「获取入口」填写联系方式，我们会按顺序发送邀请。"
            />
          </Reveal>
          <div className="mt-7">
            <GroupGrid onRequestEntry={handleRequestEntry} />
          </div>
        </section>

        <section id="submit" className="w-full scroll-mt-24 bg-background-100 py-10 md:py-14">
          <div className="mx-auto w-full px-4 md:px-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-12">
              <Reveal>
                <SectionHeading
                  eyebrow="Submit"
                  title="你也有料？直接投给我们"
                  desc="不用写得完美，把时间、地点和大概经过说清楚就够了。我们会人工核实，采纳后标注为「网友投稿」。"
                />
                <ul className="mt-6 flex flex-col gap-4">
                  {[
                    { icon: 'ri-time-line', title: '尽量写清时间线', desc: '什么时间、在哪里、发生了什么事。' },
                    { icon: 'ri-attachment-line', title: '附上可查来源', desc: '公开链接、官方账号内容都算，比口头爆料有用。' },
                    { icon: 'ri-shield-cross-line', title: '别写隐私信息', desc: '住址、联系方式、家人信息一律不接收。' },
                  ].map((item) => (
                    <li key={item.title} className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                      <i className={`${item.icon} mt-0.5 text-lg text-secondary-600`} aria-hidden="true" />
                      <span>
                        <strong className="block text-sm font-semibold text-foreground-900">{item.title}</strong>
                        <span className="mt-1 block text-xs leading-relaxed text-foreground-600">{item.desc}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal delay={80}>
                <SubmissionForm />
              </Reveal>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}